import React, { useCallback, useEffect, useRef, useState } from 'react';

import { BaseModal } from './BaseModal';
import { useTranslation } from '@i18n/client';
import { CustomCheckbox } from '@components/CustomCheckbox';
import { Textarea } from '@components/Textarea';
import { FileUploader } from '@components/FileUploader';
import { Button } from '@components/Button';
import { usePayment } from '@context/paymentContext/PaymentProvider';
import { useAlert } from '@context/AlertContext';
import { useRedirect } from '@hooks/useRedirect';
import { ROUTES } from '@constants/routes';
import { useTempApi } from '@hooks/useTempApi';

type Props = {
  isOpen: boolean;
  onClose?: () => void;
  onSubmitted?: () => void;
};

export function WhatIsWrongModal({ isOpen, onClose, onSubmitted }: Props) {
  const { t } = useTranslation();
  const { redirect } = useRedirect();
  const [files, setFiles] = useState<Array<File>>([]);
  const formRef = useRef<HTMLFormElement>(null);
  const [disabledSend, setDisabledSend] = useState(true);
  const [loading, setLoading] = useState(false);
  const {
    order: { id: orderId },
    getOrder,
  } = usePayment();
  const api = useTempApi(orderId);
  const alert = useAlert();

  const handleFilesSelected = useCallback((attachedFiles: File[]) => {
    setFiles([...attachedFiles]);
  }, []);

  const handleRemoveFile = useCallback((index: number) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  }, []);

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setLoading(true);
      const formData = new FormData(event.target as HTMLFormElement);
      let reason = '';
      formData.forEach((value, key) => {
        if (key != 'text') {
          const translated = t(`common:whatIsWrong.${key}`);
          reason += `checked: ${translated}; `;
        } else if (key === 'text' && !!`${value}`.trim()) {
          reason += `text: ${value}; `;
        }
      });

      const sendFormData = new FormData();

      if (files.length) reason += 'screenshot;';
      files.forEach((file) => {
        sendFormData.append('file', file);
      });
      sendFormData.append('reason', reason);
      if (orderId) {
        try {
          await api.order.uploadCancellation(orderId, sendFormData);
          redirect(ROUTES.feedbackSent(orderId), true);
          onSubmitted?.();
        } catch (err) {
          console.error('Error upload cancellation', orderId, err);
          alert.alert({
            message: t('shitHappens'),
            description: t('serviceIsTemporaryUnavailablePleaseTryAgainLater'),
            onClose: getOrder,
          });
        } finally {
          setLoading(false);
        }
      }
    },
    [alert, files, getOrder, orderId, redirect, t, api, onSubmitted]
  );

  const handleChange = useCallback(
  (formData: FormData) => {
    const hasValidInput = Array.from(formData.values()).some((value) => {
      if (typeof value === 'string') return !!value.trim();
      return !!value;
    });
    setDisabledSend(!hasValidInput && !files.length);
  },
  [files.length]
);

  const handleChangeForm = useCallback(
    (event: React.FormEvent) => {
      const formData = new FormData(event.currentTarget as HTMLFormElement);
      handleChange(formData);
    },
    [handleChange]
  );

  useEffect(() => {
    if (formRef.current) handleChange(new FormData(formRef.current));
  }, [files.length]);

  useEffect(() => {
    if (!isOpen) {
      if (files.length) setFiles([]);
      setDisabledSend(true);
    }
  }, [isOpen]);

  return (
    <BaseModal isOpen={isOpen} onClose={onClose}>
      <div>
        <div className='mb-6 pr-[2.875rem] text-xl font-bold text-text-primary smd:pr-0 smd:text-center'>
          {t('whatIsWrong.title')}
        </div>
        <form
          ref={formRef}
          onSubmit={handleSubmit}
          onChange={handleChangeForm}
          className='space-y-4'
        >
          <CustomCheckbox
            name='myBankBlocked'
            label={t('whatIsWrong.myBankBlocked')}
          />
          <CustomCheckbox
            name='detailsDoNotMatch'
            label={t('whatIsWrong.detailsDoNotMatch')}
          />
          <CustomCheckbox
            name='dontWantToProceed'
            label={t('whatIsWrong.dontWantToProceed')}
          />
          <CustomCheckbox name='other' label={t('whatIsWrong.other')} />
          <Textarea
            name='text'
            placeholder={t('whatIsWrong.textareaPlaceholder')}
            maxLength={255}
          />
          <FileUploader
            type='default'
            description={`${t('whatIsWrong.addScreenshot')}`}
            height='136px'
            files={files}
            onFileRemoved={handleRemoveFile}
            onFilesSelected={handleFilesSelected}
            allowTypes={['jpg', 'png', 'pdf', 'jpeg']}
            filePreviewSize={80}
            filesLimit={1}
          />
          <div className='flex flex-row items-center justify-center gap-4 pt-1 md:flex-row'>
            <Button
              type='button'
              onClick={onClose}
              full
              label={t('button.goBack')}
              variant='outlined'
            />
            <Button
              type='submit'
              disabled={disabledSend}
              isLoading={loading}
              full
              label={t('button.submit')}
            />
          </div>
        </form>
      </div>
    </BaseModal>
  );
}
